import React, { useState } from "react"
import { useMutation } from "urql"

import { Button } from "../../../components/shared/Buttons"
import { CheckBox } from "../../../components/shared/Inputs"
import { Divider, Flyout } from "../../../components/shared/Layout"
import { useToast } from "../../../contexts/ToastContext"
import CalIcon from "../ProCalendarSettings/CalIcon"

import { UPDATE_CALENDAR_CONFLICT_SELECTIONS_MUTATION } from "./graphql"
import { CONFLICT_SELECTION_EMPTY_WARNING, CONFLICT_SELECTION_TOOLTIP, conflictSelectableCalendars, providerLabel } from "./selectors"
import { CalendarConnectionSummary, MutationResultPayload } from "./types"

interface Props {
  connection: CalendarConnectionSummary | null
  visible: boolean
  onClose: () => void
  onSaved: () => void
}

const calIconProvider = (provider: string) => (provider === "microsoft" ? "live_connect" : provider)

// Conflict-calendar selection for one account. Read-only calendars (feeds)
// can be selected: conflict-only mode blocks double bookings without ever
// receiving Heal.me events.
const ReadCalendarsFlyout = ({ connection, visible, onClose, onSaved }: Props) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(
    () => connection?.calendars.filter((calendar) => calendar.selectedForConflicts).map((calendar) => calendar.id) || []
  )
  const [saving, setSaving] = useState(false)
  const [, updateSelections] = useMutation<{ updateCalendarConflictSelections: MutationResultPayload }>(
    UPDATE_CALENDAR_CONFLICT_SELECTIONS_MUTATION
  )
  const { showToast } = useToast()

  if (!connection) return null

  const calendars = conflictSelectableCalendars(connection)

  const toggle = (id: string) => {
    setSelectedIds((previous) => (previous.includes(id) ? previous.filter((v) => v !== id) : [...previous, id]))
  }

  const save = () => {
    setSaving(true)
    updateSelections({ connectionPublicId: connection.publicId, calendarIds: selectedIds }).then((result) => {
      setSaving(false)
      const payload = result.data?.updateCalendarConflictSelections
      if (payload?.result === "success") {
        showToast("Conflict calendars updated")
        onSaved()
      } else {
        showToast({ content: payload?.errors?.[0] || "Could not update conflict calendars", type: "error" })
      }
    })
  }

  return (
    <Flyout header="How should we check for conflicts?" visible={visible} closeFlyout={onClose} footer={null}>
      <div className="flex items-center gap-4">
        <CalIcon provider={calIconProvider(connection.provider)} className="" iconClassName="" />
        <div>{connection.displayIdentity || providerLabel(connection.provider)}</div>
      </div>
      <Divider className="" />
      <div className="mb-2 font-bold">Check these calendars for conflicts</div>
      <p className="mb-4 text-sm text-gray-dark">{CONFLICT_SELECTION_TOOLTIP}</p>
      {calendars.length === 0 && (
        <p className="text-sm text-gray-dark">No calendars are available on this account yet.</p>
      )}
      <ul className="flex flex-col gap-2">
        {calendars.map((calendar) => (
          <li key={calendar.id} className="flex items-center justify-between gap-2">
            <CheckBox
              className=""
              id={`conflict-calendar-${calendar.id}`}
              label={calendar.displayName || "Calendar"}
              checked={selectedIds.includes(calendar.id)}
              onChange={() => toggle(calendar.id)}
            />
            {!calendar.writable && (
              <span className="flex-none rounded bg-gray-ultralight px-2 py-0.5 text-xs text-gray-dark">Read-only</span>
            )}
          </li>
        ))}
      </ul>
      {calendars.length > 0 && selectedIds.length === 0 && (
        <p className="mt-4 rounded-lg border border-yellow bg-yellow/10 p-3 text-sm text-black" role="status">
          {CONFLICT_SELECTION_EMPTY_WARNING}
        </p>
      )}
      {connection.provider === "ical_feed" && (
        <p className="mt-4 text-sm text-gray-dark">
          Feed calendars are checked for busy times only — they never receive Heal.me bookings.
        </p>
      )}
      <div className="mt-4 flex justify-end">
        <Button onClick={save} disabled={saving} className="" floatingInMobile={false} style={{}}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </Flyout>
  )
}

export default ReadCalendarsFlyout
